/* Yen Aura · global optimistic cart drawer (24 Sep 2026 speed pass)
   ---------------------------------------------------------------
   assets/product-form.js covers the PDP, because those buy buttons sit inside a
   <product-form> element. The landing pages do NOT: each has its own
   <form action="/cart/add"> driven by lp-*.js, so they never got the benefit.

   Rather than edit every landing page script, this listens for ANY submit of a
   cart/add form in the capture phase and opens the drawer immediately, dimmed,
   while whatever script owns that form does the real work. It is purely
   additive: it adds a CSS class and calls the drawer's own open() method. If
   anything here throws, the page behaves exactly as it did before.

   18 Oct 2026 (v5.9.1): slow-connection safety net. The owner saw the drawer
   stuck on the grey skeleton on an iPhone on mobile data. A plain /cart/add
   takes 2 to 4.5s on this store even on fast broadband (the Rapi discount
   function and the gift/bundle apps run on every cart change; rendering the
   drawer itself is ~0.3s), and the add request has no time limit, so on a weak
   signal the skeleton could sit there indefinitely with no explanation.
   Now, while an add is pending:
     4s  -> "Adding to your cart..." under the skeleton (yen-slow)
     12s -> check the REAL cart: if the item landed, draw the drawer from it;
            if not, swap the skeleton for a short "taking longer than usual"
            note with a Close button (yen-stuck). It deliberately does not say
            "try again": on the PDP the Add button stays locked until the first
            request settles, so a retry tap would do nothing.
     30s -> check once more the same way
   The original request is never cancelled: if it completes later, the normal
   renderContents() path still fills and opens the drawer. product-form.js also
   calls window.yenCartFailed() when the request errors out, so a dropped
   connection shows the real cart or an error under the button, never a blank
   drawer. The old 8s backstop for the landers is replaced by the same checks. */
(function () {
  'use strict';

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function clearPending(d) {
    try {
      d.classList.remove('yen-pending', 'yen-slow', 'yen-stuck');
    } catch (e) {}
  }

  function clearTimers(d) {
    try {
      (d.__yenTimers || []).forEach(function (t) { clearTimeout(t); });
    } catch (e) {}
    d.__yenTimers = [];
  }

  // Item count shown in the header cart bubble at the moment Add was pressed.
  function bubbleCount() {
    try {
      var b = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
      var n = b ? parseInt(b.textContent, 10) : 0;
      return isNaN(n) ? 0 : n;
    } catch (e) {
      return 0;
    }
  }

  function getCart() {
    return fetch(root() + 'cart.js', { cache: 'no-store', credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  // Redraw the drawer and header bubble from the server's real cart, using the
  // drawer's own renderContents() (which also re-opens it).
  function redraw(d, cart) {
    return fetch(root() + '?sections=cart-drawer,cart-icon-bubble', {
      cache: 'no-store',
      credentials: 'same-origin',
    })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        if (!sections || !sections['cart-drawer']) throw new Error('no sections');
        clearTimers(d);
        clearPending(d);
        d.classList.toggle('is-empty', !cart.item_count);
        d.renderContents({ sections: sections });
      });
  }

  function check(d) {
    if (!d.classList.contains('yen-pending')) return;
    getCart()
      .then(function (cart) {
        if (!d.classList.contains('yen-pending')) return;
        if (cart.item_count > (d.__yenStart || 0)) return redraw(d, cart);
        d.classList.add('yen-stuck');
      })
      .catch(function () {
        if (d.classList.contains('yen-pending')) d.classList.add('yen-stuck');
      });
  }

  // Start the timers for one pending add. Called by product-form.js (PDP) and by
  // the submit listener below (landers).
  function watchAdd(d) {
    try {
      clearTimers(d);
      d.__yenStart = bubbleCount();
      d.__yenTimers = [
        setTimeout(function () {
          if (d.classList.contains('yen-pending')) d.classList.add('yen-slow');
        }, 4000),
        setTimeout(function () { check(d); }, 12000),
        setTimeout(function () { check(d); }, 30000),
      ];
    } catch (e) {}
  }
  window.yenCartWatch = watchAdd;

  // The add request itself failed (network error). If the item reached the cart
  // anyway, show it; otherwise close the drawer and let the caller show an error.
  window.yenCartFailed = function (d, onNotAdded) {
    function notAdded() {
      clearTimers(d);
      clearPending(d);
      try { d.close(); } catch (e) {}
      if (typeof onNotAdded === 'function') onNotAdded();
    }
    getCart()
      .then(function (cart) {
        if (cart.item_count > (d.__yenStart || 0)) return redraw(d, cart);
        notAdded();
      })
      .catch(notAdded);
  };

  document.addEventListener(
    'submit',
    function (e) {
      try {
        var f = e.target;
        if (!f || typeof f.matches !== 'function') return;
        if (!f.matches('form[action*="/cart/add"]')) return;
        // The PDP path is already handled inside product-form.js.
        if (f.closest && f.closest('product-form')) return;

        var d = drawer();
        if (!d || typeof d.open !== 'function') return;
        if (d.classList.contains('active')) return;

        markAdd(); // an add from a lander form may always open the drawer, even after an earlier close
        d.classList.add('yen-pending');
        watchAdd(d);
        d.open();
      } catch (err) {
        /* non-fatal: the original flow is untouched */
      }
    },
    true
  );

  // "Close" on the taking-longer note.
  document.addEventListener('click', function (e) {
    try {
      var btn = e.target && e.target.closest && e.target.closest('.yen-skel-close');
      if (!btn) return;
      var d = drawer();
      if (!d) return;
      clearTimers(d);
      clearPending(d);
      d.close();
    } catch (err) {}
  });

  /* Skeleton handling. Driven entirely off the yen-pending class so it works
     for BOTH entry points: product-form.js on the PDP and the generic submit
     listener above for the landing pages. Neither needs to know about this. */
  function showSkeleton(d) {
    try {
      if (d.querySelector('.yen-skel')) return;
      var host = d.querySelector('cart-drawer-items') || d.querySelector('.drawer__inner');
      if (!host) return;
      var s = document.createElement('div');
      s.className = 'yen-skel';
      s.innerHTML =
        '<div class="yen-skel-row" aria-hidden="true"><span class="yen-skel-img"></span>' +
        '<span class="yen-skel-lines"><i></i><i></i></span></div>' +
        '<p class="yen-skel-note yen-skel-slow" role="status">Adding to your cart…</p>' +
        '<div class="yen-skel-note yen-skel-stuck" role="status">' +
        '<p>This is taking longer than usual. Please check your connection. Your item will show here once it\'s added.</p>' +
        '<button type="button" class="yen-skel-close">Close</button></div>';
      host.appendChild(s);
    } catch (e) {}
  }

  function removeSkeleton(d) {
    try {
      var s = d.querySelectorAll('.yen-skel');
      for (var i = 0; i < s.length; i++) s[i].remove();
    } catch (e) {}
  }

  // Drop the dimmed state the moment real cart content is swapped in.
  function watch() {
    var d = drawer();
    if (!d || d.__yenWatched) return;
    d.__yenWatched = true;
    try {
      // Wait for REAL cart content before dropping the loading state.
      // Clearing on the first DOM mutation was too eager: renderContents()
      // rewrites the drawer in stages, and an intermediate state can still be
      // the empty panel, which flashed "your cart is empty" for a frame or two
      // right before the items appeared. Only a rendered .cart-item (or the
      // drawer losing is-empty) means the content is actually there.
      // 19 Oct 2026 (v5.10 audit): react only to NEW content being inserted (not to class changes and not
      // to our own skeleton). Before, when the cart already held items, the class change that STARTS the
      // pending state also ended it, so there was no "adding" state, Checkout stayed clickable on the old
      // cart, and a failed add went unnoticed.
      new MutationObserver(function (muts) {
        if (!d.classList.contains('yen-pending')) return;
        var fresh = muts.some(function (m) {
          return Array.prototype.some.call(m.addedNodes, function (n) {
            return n.nodeType === 1 && !(n.classList && n.classList.contains('yen-skel')) && !(n.closest && n.closest('.yen-skel'));
          });
        });
        if (!fresh) return;
        var hasContent =
          d.querySelector('.cart-item') || !d.classList.contains('is-empty');
        if (hasContent) {
          clearTimers(d);
          clearPending(d);
          removeSkeleton(d);
        }
      }).observe(d, { childList: true, subtree: true });

      // Show or tear down the skeleton purely in response to the pending class.
      new MutationObserver(function () {
        if (d.classList.contains('yen-pending')) showSkeleton(d);
        else removeSkeleton(d);
      }).observe(d, { attributes: true, attributeFilter: ['class'] });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }

  /* 19 Oct 2026 (owner, v5.10): "I press the X and the cart opens again." The drawer's renderContents()
     always ends with open(), and the Bogos gift app calls renderContents() whenever it refreshes the cart
     (every second while a free gift sat above quantity 1), so a close was undone by the next refresh.
     Rule now: once the shopper closes the drawer, a refresh may update what is inside but may only OPEN it
     again after a new add-to-cart intent (an add request, an add form, or a tap on an Add button) or when
     the shopper opens it themselves (the cart icon passes itself to open()). */
  function markAdd() {
    window.__yenAddAt = Date.now();
  }
  // A network add only counts as the shopper's intent when it follows a real tap within 4s. The Bogos app
  // adds the free gift on its own; that must not re-open a drawer the shopper has just closed.
  function recentTap() {
    return window.__yenTapAt && Date.now() - window.__yenTapAt < 4000;
  }
  ['pointerdown', 'keydown'].forEach(function (type) {
    document.addEventListener(type, function () { window.__yenTapAt = Date.now(); }, true);
  });
  try {
    var _fetch = window.fetch;
    if (typeof _fetch === 'function') {
      window.fetch = function (input, init) {
        try {
          var u = String(typeof input === 'string' ? input : (input && input.url) || '');
          if (u.indexOf('/cart/add') !== -1 && recentTap()) markAdd();
          // 19 Oct 2026 (v5.10 audit): the drawer's -, + and delete send the line's POSITION ("line": 2).
          // When the Bogos gift is added or removed, positions shift before the drawer redraws, so a tap
          // could change the wrong row (e.g. raise the free gift). Send the exact line key of the row the
          // shopper actually tapped instead.
          if (u.indexOf('/cart/change') !== -1 && init && typeof init.body === 'string') {
            var b = JSON.parse(init.body);
            if (b && b.line && !b.id) {
              var row = document.getElementById('CartDrawer-Item-' + b.line);
              var key = row && row.getAttribute('data-yen-line-key');
              if (key) {
                delete b.line;
                b.id = key;
                arguments[1] = Object.assign({}, init, { body: JSON.stringify(b) });
              }
            }
          }
        } catch (e) {}
        return _fetch.apply(this, arguments);
      };
    }
    var _xhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (String(url).indexOf('/cart/add') !== -1 && recentTap()) markAdd();
      } catch (e) {}
      return _xhrOpen.apply(this, arguments);
    };
  } catch (e) {}
  document.addEventListener(
    'submit',
    function (e) {
      try {
        if (e.target && e.target.matches && e.target.matches('form[action*="/cart/add"]')) markAdd();
      } catch (err) {}
    },
    true
  );
  document.addEventListener(
    'click',
    function (e) {
      try {
        var b = e.target && e.target.closest && e.target.closest('button, a, [role="button"], input[type="submit"]');
        if (!b || (b.closest && b.closest('.drawer__close'))) return;
        var label = ((b.className || '') + ' ' + (b.getAttribute('name') || '') + ' ' + (b.getAttribute('aria-label') || '') + ' ' + (b.textContent || '')).toLowerCase();
        if (/\badd\b|add-to-cart|addtocart|add_to_cart|\bbuy\b|start (clearing|fading|my)/.test(label)) markAdd();
      } catch (err) {}
    },
    true
  );
  // Tapping the dimmed backdrop closes the drawer through a handler bound before the patch below, so
  // record that close here too.
  document.addEventListener(
    'click',
    function (e) {
      try {
        if (e.target && e.target.closest && e.target.closest('#CartDrawer-Overlay, .cart-drawer__overlay')) {
          var d = drawer();
          if (d) d.__yenClosedAt = Date.now();
        }
      } catch (err) {}
    },
    true
  );
  customElements.whenDefined('cart-drawer').then(function () {
    // The old gift-wrapping feature is not set up on this store (no data-shipping-pro on <body>), yet its
    // shipping_pro() still fetched /cart.js on every drawer render. Switch it off while it is unused.
    try {
      if (!document.body.getAttribute('data-shipping-pro') && typeof window.shipping_pro === 'function') {
        window.shipping_pro = function () {};
      }
    } catch (e) {}
    try {
      var proto = customElements.get('cart-drawer').prototype;
      var origOpen = proto.open;
      var origClose = proto.close;
      proto.close = function () {
        this.__yenClosedAt = Date.now();
        return origClose.apply(this, arguments);
      };
      proto.open = function (triggeredBy) {
        if (!triggeredBy && this.__yenClosedAt && this.__yenClosedAt > (window.__yenAddAt || 0)) return;
        return origOpen.apply(this, arguments);
      };
    } catch (e) {}
  });
})();
